import { useTranslation } from 'react-i18next';
import { Target, Sparkles, AlertTriangle, Zap, Lightbulb, Activity, Coins, ChevronsUp, Trophy, Ban } from 'lucide-react';
import { Page } from '@/components/layout/Page';
import { PageHeader } from '@/components/layout/PageHeader';
import { PageContent } from '@/components/layout/PageContent';
import { cn } from '@/lib/utils';

const SCORING_GRID = [
    { key: 'exact', points: '+10', positive: true },
    { key: 'offBy1', points: '+6', positive: true },
    { key: 'offBy2', points: '+3', positive: true },
    { key: 'offBy3', points: '+1', positive: true },
    { key: 'offBy4_5', points: '-1', positive: false },
    { key: 'offBy6Plus', points: '-2', positive: false },
] as const;

const EFFECTIVE_INDEX_FACTORS = [
    { key: 'base', color: 'bg-blue-50 text-blue-600 border-blue-100' },
    { key: 'experience', color: 'bg-emerald-50 text-emerald-600 border-emerald-100' },
    { key: 'distance', color: 'bg-amber-50 text-amber-600 border-amber-100' },
    { key: 'dnf', color: 'bg-red-50 text-red-600 border-red-100' },
] as const;

export function ScoringPage() {
    const { t } = useTranslation('scoring');

    return (
        <Page className="bg-slate-50">
            <PageHeader title={t('title')} backLink="/" width="narrow" />

            <PageContent width="narrow">
                <div className="space-y-6 pb-8">

                    {/* --- INTRO --- */}
                    <div className="animate-section animate-section-1 bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-3xl p-6 text-white">
                        <p className="text-emerald-100 text-sm leading-relaxed">
                            {t('subtitle')}
                        </p>
                    </div>

                    {/* --- POSITION SCORING --- */}
                    <div className="animate-section animate-section-2 space-y-3">
                        <div className="flex items-center gap-2 pl-1">
                            <Target size={18} className="text-emerald-500" />
                            <h2 className="text-sm font-bold text-slate-400 uppercase tracking-widest">
                                {t('position.title')}
                            </h2>
                        </div>

                        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
                            <p className="px-4 pt-4 pb-3 text-sm text-slate-600">
                                {t('position.description')}
                            </p>

                            <div className="divide-y divide-slate-100">
                                {SCORING_GRID.map((row) => (
                                    <div key={row.key} className="flex items-center justify-between px-4 py-3">
                                        <span className="text-sm font-medium text-slate-700">
                                            {t(`position.${row.key}`)}
                                        </span>
                                        <span className={cn(
                                            'text-sm font-black px-3 py-1 rounded-lg',
                                            row.positive
                                                ? 'bg-emerald-50 text-emerald-600'
                                                : 'bg-red-50 text-red-500'
                                        )}>
                                            {row.points} pts
                                        </span>
                                    </div>
                                ))}
                            </div>

                            <div className="px-4 py-3 bg-emerald-50 border-t border-emerald-100">
                                <p className="text-xs text-emerald-700 leading-relaxed">
                                    {t('position.example')}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* --- SURPRISE BONUS --- */}
                    <div className="animate-section animate-section-3 space-y-3">
                        <div className="flex items-center gap-2 pl-1">
                            <Sparkles size={18} className="text-indigo-500" />
                            <h2 className="text-sm font-bold text-slate-400 uppercase tracking-widest">
                                {t('surprise.title')}
                            </h2>
                        </div>

                        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
                            <p className="px-4 pt-4 pb-3 text-sm text-slate-600">
                                {t('surprise.description')}
                            </p>

                            {/* Formula block */}
                            <div className="mx-4 mb-3 bg-indigo-50 border border-indigo-100 rounded-2xl p-4 text-center">
                                <div className="text-base font-black text-indigo-900">
                                    <span className="text-indigo-400">multiplier</span>
                                    {' = min( 1 + 8 × '}
                                    <span className="inline-block px-2 py-0.5 bg-indigo-100 rounded-lg">
                                        {'√('}
                                        <span className="text-indigo-600">{t('surprise.formulaGap')}</span>
                                        {' / '}
                                        <span className="text-indigo-600">{t('surprise.formulaTotal')}</span>
                                        {')'}
                                    </span>
                                    {' , 3 )'}
                                </div>
                            </div>

                            <div className="mx-4 mb-3 px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl">
                                <p className="text-xs text-slate-600 leading-relaxed">
                                    {t('surprise.gapExplanation')}
                                </p>
                            </div>

                            <div className="px-4 py-3 bg-indigo-50 border-t border-indigo-100 space-y-2">
                                <p className="text-xs text-indigo-700 leading-relaxed">
                                    {t('surprise.example')}
                                </p>
                                <p className="text-xs text-indigo-700 leading-relaxed">
                                    {t('surprise.example2')}
                                </p>
                            </div>
                        </div>

                        <div className="bg-slate-50 rounded-xl border border-slate-200 px-4 py-3 space-y-2">
                            <p className="text-xs text-slate-500 leading-relaxed">
                                {t('surprise.onlyPositive')}
                            </p>
                            <p className="text-xs text-slate-500 leading-relaxed">
                                {t('surprise.smallField')}
                            </p>
                        </div>
                    </div>

                    {/* --- DNF --- */}
                    <div className="animate-section animate-section-4 space-y-3">
                        <div className="flex items-center gap-2 pl-1">
                            <AlertTriangle size={18} className="text-amber-500" />
                            <h2 className="text-sm font-bold text-slate-400 uppercase tracking-widest">
                                {t('dnf.title')}
                            </h2>
                        </div>

                        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
                            <p className="px-4 pt-4 pb-3 text-sm text-slate-600">
                                {t('dnf.description')}
                            </p>

                            <div className="divide-y divide-slate-100">
                                <div className="px-4 py-3">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="text-xs font-black px-2 py-0.5 rounded-lg bg-emerald-50 text-emerald-600">
                                            +10 pts
                                        </span>
                                        <span className="text-sm font-bold text-slate-700">
                                            {t('dnf.correct.title')}
                                        </span>
                                    </div>
                                    <p className="text-xs text-slate-500 leading-relaxed mb-2">
                                        {t('dnf.correct.description')}
                                    </p>

                                    {/* DNF formula */}
                                    <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 mb-2 text-center">
                                        <div className="text-sm font-black text-amber-900">
                                            {t('dnf.correct.formula')}
                                        </div>
                                    </div>

                                    <div className="space-y-1.5">
                                        <p className="text-xs text-slate-500 leading-relaxed">
                                            {t('dnf.correct.example1')}
                                        </p>
                                        <p className="text-xs text-slate-500 leading-relaxed">
                                            {t('dnf.correct.example2')}
                                        </p>
                                    </div>
                                </div>

                                <div className="px-4 py-3">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="text-xs font-black px-2 py-0.5 rounded-lg bg-red-50 text-red-500">
                                            -1 pt
                                        </span>
                                        <span className="text-sm font-bold text-slate-700">
                                            {t('dnf.mismatch.title')}
                                        </span>
                                    </div>
                                    <p className="text-xs text-slate-500 leading-relaxed">
                                        {t('dnf.mismatch.description')}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* --- EFFECTIVE INDEX / RUNNER EVALUATION --- */}
                    <div className="animate-section animate-section-5 space-y-3">
                        <div className="flex items-center gap-2 pl-1">
                            <Activity size={18} className="text-violet-500" />
                            <h2 className="text-sm font-bold text-slate-400 uppercase tracking-widest">
                                {t('effectiveIndex.title')}
                            </h2>
                        </div>

                        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
                            <p className="px-4 pt-4 pb-3 text-sm text-slate-600">
                                {t('effectiveIndex.description')}
                            </p>

                            {/* 4 Factors */}
                            <div className="px-4 pb-3">
                                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">
                                    {t('effectiveIndex.factors.title')}
                                </p>
                                <div className="space-y-2">
                                    {EFFECTIVE_INDEX_FACTORS.map((factor) => (
                                        <div key={factor.key} className={cn('rounded-xl border p-3', factor.color)}>
                                            <p className="text-xs font-bold mb-0.5">
                                                {t(`effectiveIndex.factors.${factor.key}.label`)}
                                            </p>
                                            <p className="text-xs opacity-80 leading-relaxed">
                                                {t(`effectiveIndex.factors.${factor.key}.description`)}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Formula */}
                            <div className="mx-4 mb-3 bg-violet-50 border border-violet-100 rounded-2xl p-4 text-center">
                                <div className="text-sm font-black text-violet-900 leading-relaxed">
                                    {t('effectiveIndex.formula')}
                                </div>
                            </div>

                            {/* Concrete example */}
                            <div className="mx-4 mb-3 bg-slate-50 border border-slate-200 rounded-xl p-3">
                                <p className="text-xs font-bold text-slate-700 mb-1">
                                    {t('effectiveIndex.example.title')}
                                </p>
                                <p className="text-xs text-slate-500 leading-relaxed mb-2">
                                    {t('effectiveIndex.example.description')}
                                </p>
                                <div className="space-y-1">
                                    <p className="text-xs text-slate-600">
                                        <span className="font-semibold">1.</span> {t('effectiveIndex.example.step1')}
                                    </p>
                                    <p className="text-xs text-slate-600">
                                        <span className="font-semibold">2.</span> {t('effectiveIndex.example.step2')}
                                    </p>
                                    <p className="text-xs text-slate-600">
                                        <span className="font-semibold">3.</span> {t('effectiveIndex.example.step3')}
                                    </p>
                                    <p className="text-xs text-slate-600">
                                        <span className="font-semibold">4.</span> {t('effectiveIndex.example.step4')}
                                    </p>
                                </div>
                                <div className="mt-2 pt-2 border-t border-slate-200">
                                    <p className="text-xs font-bold text-violet-700">
                                        {t('effectiveIndex.example.result')}
                                    </p>
                                </div>
                            </div>

                            <div className="px-4 py-3 bg-violet-50 border-t border-violet-100">
                                <p className="text-xs text-violet-700 leading-relaxed">
                                    {t('effectiveIndex.rankExplanation')}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* --- TOKENS --- */}
                    <div className="animate-section animate-section-6 space-y-3">
                        <div className="flex items-center gap-2 pl-1">
                            <Coins size={18} className="text-amber-500" />
                            <h2 className="text-sm font-bold text-slate-400 uppercase tracking-widest">
                                {t('tokens.title')}
                            </h2>
                        </div>
                        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 space-y-2">
                            <p className="text-sm text-slate-600">{t('tokens.description')}</p>
                            <p className="text-xs text-slate-500">{t('tokens.mandatory')}</p>
                        </div>
                    </div>

                    {/* --- DOUBLE DOWN --- */}
                    <div className="animate-section animate-section-7 space-y-3">
                        <div className="flex items-center gap-2 pl-1">
                            <ChevronsUp size={18} className="text-amber-600" />
                            <h2 className="text-sm font-bold text-slate-400 uppercase tracking-widest">
                                {t('doubleDown.title')}
                            </h2>
                        </div>
                        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
                            <p className="px-4 pt-4 pb-3 text-sm text-slate-600">{t('doubleDown.description')}</p>
                            <div className="divide-y divide-slate-100">
                                <div className="flex items-center justify-between px-4 py-3">
                                    <span className="text-sm text-slate-700">{t('doubleDown.exact')}</span>
                                    <span className="text-sm font-black px-3 py-1 rounded-lg bg-emerald-50 text-emerald-600">+20 pts</span>
                                </div>
                                <div className="flex items-center justify-between px-4 py-3">
                                    <span className="text-sm text-slate-700">{t('doubleDown.offBy1')}</span>
                                    <span className="text-sm font-black px-3 py-1 rounded-lg bg-slate-50 text-slate-500">6 pts</span>
                                </div>
                                <div className="flex items-center justify-between px-4 py-3">
                                    <span className="text-sm text-slate-700">{t('doubleDown.offBy2')}</span>
                                    <span className="text-sm font-black px-3 py-1 rounded-lg bg-red-50 text-red-500">&lt; normal</span>
                                </div>
                            </div>
                            <div className="px-4 py-3 bg-amber-50 border-t border-amber-100">
                                <p className="text-xs text-amber-700 leading-relaxed">{t('doubleDown.warning')}</p>
                            </div>
                        </div>
                    </div>

                    {/* --- BONUSES --- */}
                    <div className="animate-section animate-section-8 space-y-3">
                        <div className="flex items-center gap-2 pl-1">
                            <Trophy size={18} className="text-emerald-500" />
                            <h2 className="text-sm font-bold text-slate-400 uppercase tracking-widest">
                                {t('bonus.title')}
                            </h2>
                        </div>
                        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
                            <p className="px-4 pt-4 pb-3 text-sm text-slate-600">{t('bonus.description')}</p>
                            <div className="divide-y divide-slate-100">
                                <div className="px-4 py-3">
                                    <p className="text-sm font-bold text-slate-700 mb-1">{t('bonus.topN.title')}</p>
                                    <p className="text-xs text-slate-500 mb-2">{t('bonus.topN.description')}</p>
                                    <div className="space-y-1">
                                        <p className="text-xs text-emerald-600 font-semibold">{t('bonus.topN.top3')}</p>
                                        <p className="text-xs text-emerald-600 font-semibold">{t('bonus.topN.top5')}</p>
                                        <p className="text-xs text-emerald-600 font-semibold">{t('bonus.topN.top10')}</p>
                                    </div>
                                </div>
                                <div className="px-4 py-3">
                                    <p className="text-sm font-bold text-slate-700 mb-1">{t('bonus.dnf.title')}</p>
                                    <p className="text-xs text-slate-500 mb-2">{t('bonus.dnf.description')}</p>
                                    <div className="space-y-1">
                                        <p className="text-xs text-emerald-600 font-semibold">{t('bonus.dnf.dnf3')}</p>
                                        <p className="text-xs text-emerald-600 font-semibold">{t('bonus.dnf.dnf5')}</p>
                                    </div>
                                    <p className="text-xs text-slate-400 mt-2">{t('bonus.dnf.condition')}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* --- DNS --- */}
                    <div className="animate-section animate-section-9 space-y-3">
                        <div className="flex items-center gap-2 pl-1">
                            <Ban size={18} className="text-slate-400" />
                            <h2 className="text-sm font-bold text-slate-400 uppercase tracking-widest">
                                {t('dns.title')}
                            </h2>
                        </div>
                        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 space-y-2">
                            <p className="text-sm text-slate-600">{t('dns.description')}</p>
                            <p className="text-xs text-slate-500">{t('dns.compaction')}</p>
                        </div>
                    </div>

                    {/* --- POTENTIAL SCORE --- */}
                    <div className="animate-section animate-section-6 bg-indigo-50 border border-indigo-100 rounded-2xl p-4 flex items-start gap-3">
                        <div className="w-8 h-8 rounded-xl bg-indigo-100 flex items-center justify-center flex-shrink-0">
                            <Zap size={16} className="text-indigo-600" />
                        </div>
                        <div>
                            <h3 className="text-sm font-bold text-indigo-900 mb-1">{t('potential.title')}</h3>
                            <p className="text-xs text-indigo-700 leading-relaxed">
                                {t('potential.description')}
                            </p>
                        </div>
                    </div>

                    {/* --- TIPS --- */}
                    <div className="animate-section animate-section-7 space-y-3">
                        <div className="flex items-center gap-2 pl-1">
                            <Lightbulb size={18} className="text-amber-500" />
                            <h2 className="text-sm font-bold text-slate-400 uppercase tracking-widest">
                                {t('tips.title')}
                            </h2>
                        </div>

                        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 space-y-3">
                            {[1, 2, 3, 4].map((n) => (
                                <div key={n} className="flex items-start gap-3">
                                    <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                                        <span className="text-xs font-bold text-slate-400">{n}</span>
                                    </div>
                                    <p className="text-sm text-slate-600 leading-relaxed">
                                        {t(`tips.tip${n}`)}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>

                </div>
            </PageContent>
        </Page>
    );
}
